import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Archive, ArchiveRestore, MailPlus, Pencil, RefreshCcw, UserMinus } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import type { PoolMemberRow } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { Popover, usePopover } from '../../../components/Popover';
import { Segmented } from '../../../components/Segmented';
import { SkeletonLines } from '../../../components/Skeleton';
import { ARCHIVED_TONE } from '../../../components/status-tones';
import { EmptyState, FilteredEmptyState, ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { ToneChip } from '../../../components/Tone';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { errorMessage } from '../../../lib/api-client';
import { formatDate, formatNumber, formatRelative } from '../../../lib/format';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { poolsApi, usePool } from '../api/pools-api';
import { InviteToJobPanel, PoolForm, usePoolRefresh } from '../components/PoolPanels';
import styles from '../Pools.module.css';
import fit from '../../../components/FitPage.module.css';
import { goneId, useRecordVisit } from '../../../lib/use-record-visit';
import { useStageLabels } from '../../../lib/modules';

export interface PoolPageSearch {
  readonly filter?: 'expiring';
}

/**
 * POOL-02 — "members list; per member: why pooled, by whom, from which application, expiry;
 * actions: invite to a job, message, remove" (E12). Message arrives with S26's compose. A
 * member's application is shown as it ended — pooling never changes it.
 */
export function PoolPage({ poolId }: { readonly poolId: string }): ReactNode {
  const stageLabels = useStageLabels();
  const canView = useCanView('talent_pool');
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as PoolPageSearch;
  const pool = usePool(poolId);
  useRecordVisit(pool.data ? { kind: 'pool', id: poolId, title: pool.data.pool.name, subtitle: 'Talent pool', path: `/pools/${poolId}` } : null, goneId(pool.error, poolId));
  const refresh = usePoolRefresh();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [inviting, setInviting] = useState<{ readonly ids: readonly string[]; readonly anchor: AnchorRect } | null>(null);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const { anchor: editAnchor, triggerRef: editTrigger, toggle: toggleEdit, close: closeEdit } = usePopover();

  if (!canView) return <ForbiddenState resource="talent pools" contact="Your admin" />;
  if (pool.error && !pool.data) return pool.error.status === 404 ? <NotFoundState onGoBack={() => void navigate({ to: '/pools' })} /> : <ServerErrorState onRetry={() => void pool.refetch()} />;
  if (!pool.data) {
    return (
      <div className={styles.page}>
        <SkeletonLines count={8} />
      </div>
    );
  }

  const view = pool.data;
  const isExpiringFilter = search.filter === 'expiring';
  const members = sortMembers(isExpiringFilter ? view.members.filter((member) => member.isExpiringSoon) : view.members, sort);
  const chosen = selection.mode === 'all-matching' ? members.filter((member) => !selection.excluded.has(member.membershipId)) : members.filter((member) => selection.ids.has(member.membershipId));

  const runOn = async (rows: readonly PoolMemberRow[], action: 'renew' | 'remove'): Promise<void> => {
    if (rows.length === 0) return;
    const ids = rows.map((row) => row.membershipId);
    setBusy(new Set(ids));
    try {
      if (action === 'renew') {
        const result = await poolsApi.renewConsent(poolId, { membershipIds: ids });
        await refresh();
        show({ tone: 'success', message: result.renewed === 1 ? 'Consent renewed for 24 months' : `Consent renewed for ${result.renewed} people`, detail: 'Their 24 months start again today.' });
      } else {
        await poolsApi.removeMembers(poolId, { membershipIds: ids });
        await refresh();
        setSelection(EMPTY_SELECTION);
        showUndo(rows.length === 1 ? `${rows[0]?.candidate.fullName ?? 'They'} removed from the pool` : `${rows.length} people removed from the pool`, () => {
          void poolsApi
            .addMembers(poolId, { candidateIds: rows.map((row) => row.candidate.id), note: 'Put back after a removal was undone' })
            .then(refresh)
            .catch((cause: unknown) => show({ tone: 'danger', message: "They couldn't be put back", detail: errorMessage(cause) }));
        });
      }
    } catch (cause) {
      show({ tone: 'danger', message: action === 'renew' ? "Consent wasn't renewed" : "They weren't removed", detail: errorMessage(cause) });
    } finally {
      setBusy(new Set());
    }
  };

  const setArchived = async (isArchiving: boolean): Promise<void> => {
    try {
      await (isArchiving ? poolsApi.archive(poolId, view.pool.version) : poolsApi.restore(poolId, view.pool.version));
      await refresh();
      if (isArchiving) showUndo(`${view.pool.name} archived`, () => void poolsApi.restore(poolId, view.pool.version + 1).then(refresh));
      else show({ tone: 'success', message: `${view.pool.name} restored` });
    } catch (cause) {
      show({ tone: 'danger', message: isArchiving ? "The pool wasn't archived" : "The pool wasn't restored", detail: errorMessage(cause) });
    }
  };

  const columns: readonly Column<PoolMemberRow>[] = [
    {
      id: 'name',
      header: 'Name',
      isIdentity: true,
      isSortable: true,
      defaultWidth: 230,
      cell: (row) => (
        <span className={styles.stack}>
          <Link to="/candidates/$candidateId" params={{ candidateId: row.candidate.id }} className={styles.name}>
            {row.candidate.fullName}
          </Link>
          {row.candidate.currentTitle && <span className={styles.meta}>{row.candidate.currentTitle}</span>}
          {row.candidate.doNotRehire && (
            <Chip tone="danger" plain>
              Do not rehire
            </Chip>
          )}
        </span>
      ),
      exportValue: (row) => row.candidate.fullName,
    },
    { id: 'reason', header: 'Why', defaultWidth: 220, cell: (row) => row.reason },
    {
      id: 'source',
      header: 'Came from',
      defaultWidth: 260,
      cell: (row) =>
        row.sourceApplication ? (
          <span className={styles.stack}>
            <Link to="/applications/$applicationId" params={{ applicationId: row.sourceApplication.id }} className={styles.name}>
              {row.sourceApplication.jobTitle}
            </Link>
            <span className={styles.meta}>
              {stageLabels[row.sourceApplication.stage]}
              {row.sourceApplication.reasonLabel ? ` · ${row.sourceApplication.reasonLabel}` : ''}
            </span>
          </span>
        ) : null,
      exportValue: (row) =>
        row.sourceApplication
          ? [row.sourceApplication.jobTitle, stageLabels[row.sourceApplication.stage], row.sourceApplication.reasonLabel].filter(Boolean).join(' · ')
          : null,
    },
    {
      id: 'added',
      header: 'Added',
      isSortable: true,
      defaultWidth: 160,
      cell: (row) => (
        <span className={styles.stack}>
          <span>{formatDate(row.addedAt)}</span>
          <span className={styles.meta}>{row.addedByName ?? 'The system'}</span>
        </span>
      ),
      exportValue: (row) => `${formatDate(row.addedAt)} · ${row.addedByName ?? 'The system'}`,
    },
    {
      id: 'expires',
      header: 'Consent until',
      isSortable: true,
      defaultWidth: 160,
      cell: (row) =>
        row.isExpiringSoon ? (
          <Chip tone="warning" plain>
            {formatDate(row.expiresAt)}
          </Chip>
        ) : (
          formatDate(row.expiresAt)
        ),
      exportValue: (row) => formatDate(row.expiresAt),
    },
    { id: 'contacted', header: 'Last contacted', isSortable: true, defaultWidth: 140, cell: (row) => (row.lastContactedAt ? formatRelative(row.lastContactedAt) : null) },
  ];

  return (
    <div className={`${styles.page} ${fit.fit}`}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <span className={styles.meta}>
            <Link to="/pools" className={styles.name}>
              Talent pools
            </Link>{' '}
            / {view.pool.isSystem ? 'System pool' : `Owned by ${view.pool.ownerName}`}
          </span>
          <h1 className={styles.title}>{view.pool.name}</h1>
          {view.pool.description && <p className={styles.lead}>{view.pool.description}</p>}
          {view.pool.isArchived && <ToneChip tone={ARCHIVED_TONE}>Archived — not offered in pickers or job matches</ToneChip>}
        </div>
        {view.can.edit && (
          <div className={styles.actions}>
            <Button ref={editTrigger} variant="outline" icon={Pencil} onClick={toggleEdit}>
              Edit
            </Button>
            {view.can.archive &&
              (view.pool.isArchived ? (
                <Button variant="outline" icon={ArchiveRestore} onClick={() => void setArchived(false)}>
                  Restore
                </Button>
              ) : (
                <Button variant="ghost" icon={Archive} onClick={() => void setArchived(true)}>
                  Archive
                </Button>
              ))}
          </div>
        )}
      </header>

      <div className={styles.facts}>
        <Fact label="Members" value={formatNumber(view.pool.memberCount)} />
        <Fact label="Contacted in 6 months" value={view.pool.freshnessPercent === null ? '—' : `${view.pool.freshnessPercent}%`} />
        <Fact label="Consent ending in 30 days" value={formatNumber(view.pool.expiringSoonCount)} />
        <Fact label="Created" value={formatDate(view.pool.createdAt)} />
      </div>

      <div className={styles.actions}>
        <Segmented
          label="Members"
          value={isExpiringFilter ? 'expiring' : 'all'}
          options={[
            { value: 'all', label: 'Everyone' },
            { value: 'expiring', label: `Consent ending soon (${view.pool.expiringSoonCount})` },
          ]}
          onChange={(value) => void navigate({ to: '/pools/$poolId', params: { poolId }, search: value === 'expiring' ? { filter: 'expiring' } : {}, replace: true })}
        />
      </div>

      <DataTable
        screenId="pool-members"
        label={`Members of ${view.pool.name}`}
        columns={columns}
        rows={members}
        getRowId={(row) => row.membershipId}
        total={members.length}
        sort={sort}
        onSortChange={setSort}
        selection={selection}
        onSelectionChange={setSelection}
        onOpenRow={(row) => void navigate({ to: '/candidates/$candidateId', params: { candidateId: row.candidate.id } })}
        linkOf={(row) => `/candidates/${row.candidate.id}`}
        exportName="pool-members"
        views={{
          screen: 'pool',
          filters: search.filter === 'expiring' ? { filter: search.filter } : {},
          onApply: (filters) => void navigate({ to: '/pools/$poolId', params: { poolId }, search: filters.filter === 'expiring' ? { filter: 'expiring' } : {}, replace: true }),
        }}
        rowActions={
          view.can.edit
            ? (row) => (
                <>
                  <Button variant="quiet" size="sm" icon={MailPlus} onClick={(event: MouseEvent<HTMLButtonElement>) => setInviting({ ids: [row.candidate.id], anchor: anchorFrom(event.currentTarget) })}>
                    Invite
                  </Button>
                  <Button variant="quiet" size="sm" icon={RefreshCcw} isPending={busy.has(row.membershipId)} onClick={() => void runOn([row], 'renew')}>
                    Consent renewed
                  </Button>
                  <Button variant="quiet" size="sm" icon={UserMinus} isPending={busy.has(row.membershipId)} aria-label={`Remove ${row.candidate.fullName}`} onClick={() => void runOn([row], 'remove')} />
                </>
              )
            : undefined
        }
        bulkActions={
          view.can.edit ? (
            <>
              <Button variant="quiet" size="sm" icon={MailPlus} disabled={chosen.length === 0} onClick={(event: MouseEvent<HTMLButtonElement>) => setInviting({ ids: chosen.map((row) => row.candidate.id), anchor: anchorFrom(event.currentTarget) })}>
                Invite to a job
              </Button>
              <Button variant="quiet" size="sm" icon={RefreshCcw} disabled={chosen.length === 0} onClick={() => void runOn(chosen, 'renew')}>
                Consent renewed
              </Button>
              <Button variant="quiet" size="sm" icon={UserMinus} disabled={chosen.length === 0} onClick={() => void runOn(chosen, 'remove')}>
                Remove
              </Button>
            </>
          ) : undefined
        }
        emptyState={
          isExpiringFilter ? (
            <FilteredEmptyState filterCount={1} onClearFilters={() => void navigate({ to: '/pools/$poolId', params: { poolId }, search: {}, replace: true })} />
          ) : (
            <EmptyState
              title="Nobody in this pool yet"
              body="Add people from All candidates, a candidate's profile, or when you reject someone in sorting or the pipeline."
              action={
                <Button variant="outline" onClick={() => void navigate({ to: '/candidates' })}>
                  Search candidates
                </Button>
              }
            />
          )
        }
      />

      <Popover anchor={editAnchor} onClose={closeEdit} align="right" width={380} label="Edit pool">
        <PoolForm pool={view.pool} onCancel={closeEdit} onDone={closeEdit} />
      </Popover>
      <Popover anchor={inviting?.anchor ?? null} onClose={() => setInviting(null)} align="right" width={400} label="Invite to a job">
        {inviting && (
          <InviteToJobPanel
            candidateIds={inviting.ids}
            onCancel={() => setInviting(null)}
            onDone={() => {
              setInviting(null);
              setSelection(EMPTY_SELECTION);
            }}
          />
        )}
      </Popover>
    </div>
  );
}

function Fact({ label, value }: { readonly label: string; readonly value: string }): ReactNode {
  return (
    <div className={styles.fact}>
      <span className={styles.factLabel}>{label}</span>
      <span className={styles.factValue}>{value}</span>
    </div>
  );
}

const MEMBER_SORTERS: Readonly<Record<string, (row: PoolMemberRow) => string>> = {
  name: (row) => row.candidate.fullName.toLowerCase(),
  added: (row) => row.addedAt,
  expires: (row) => row.expiresAt,
  contacted: (row) => row.lastContactedAt ?? '',
};

function sortMembers(rows: readonly PoolMemberRow[], sort: readonly SortSpec[]): readonly PoolMemberRow[] {
  if (sort.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const spec of sort) {
      const read = MEMBER_SORTERS[spec.columnId];
      if (!read) continue;
      const left = read(a);
      const right = read(b);
      if (left === right) continue;
      const order = left < right ? -1 : 1;
      return spec.direction === 'asc' ? order : -order;
    }
    return 0;
  });
}
